import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, Building2, FileText, FileSignature,
    CalendarClock, Receipt, Boxes, Folder, BarChart3, Settings, LogOut,
    Menu, ChevronLeft, Landmark, Wallet, BookOpen, Kanban, Lightbulb, ShieldCheck,
    ShoppingCart, Users, HeartHandshake, Clock, LifeBuoy,
    Search as SearchIcon, Database, GitBranch, Repeat, X, TrendingUp, Upload, Inbox as InboxIcon, Send
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BrandLogo from './BrandLogo';
import NotificationBell from './NotificationBell';

const NAV_GROUPS = [
    {
        label: null,
        items: [
            { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, perm: 'dashboard' },
        ],
    },
    {
        label: 'CRM & Sales',
        items: [
            { to: '/clients',   label: 'Clients',   icon: Building2,     perm: 'crm' },
            { to: '/proposals', label: 'Proposals', icon: FileText,      perm: 'proposals' },
            { to: '/contracts', label: 'Contracts', icon: FileSignature, perm: 'proposals' },
            { to: '/ndas',      label: 'NDAs',      icon: ShieldCheck,   perm: 'proposals' },
            { to: '/renewals',  label: 'Renewals',  icon: CalendarClock, perm: 'proposals' },
        ],
    },
    {
        label: 'Delivery',
        items: [
            { to: '/projects',  label: 'Projects',         icon: Kanban,  perm: 'projects' },
            { to: '/backlog',   label: 'Ideas & Backlog',  icon: Lightbulb, perm: 'projects' },
            { to: '/documents', label: 'Documents',        icon: Folder,  perm: 'projects' },
            { to: '/modules',   label: 'Modules',          icon: Boxes,   perm: 'projects' },
            { to: '/support-hub', label: 'Support Hub',   icon: LifeBuoy, perm: 'support' },
        ],
    },
    {
        label: 'Finance',
        items: [
            { to: '/invoices',    label: 'Invoices',        icon: Receipt,      perm: 'finance' },
            { to: '/recurring',   label: 'Recurring',       icon: Repeat,       perm: 'finance' },
            { to: '/purchases',   label: 'Purchases',       icon: ShoppingCart, comingSoon: true },
            { to: '/monzo',       label: 'Monzo',           icon: Landmark,     perm: 'finance' },
            { to: '/bookkeeping', label: 'Bookkeeping',     icon: Wallet,       perm: 'finance' },
            { to: '/ledger',      label: 'General Ledger',  icon: BookOpen,     perm: 'finance' },
            { to: '/reports',     label: 'Reports',         icon: BarChart3,    perm: 'finance' },
            { to: '/profitability', label: 'Profitability', icon: TrendingUp,   perm: 'finance' },
        ],
    },
    {
        label: 'People',
        items: [
            { to: '/payroll',     label: 'Payroll',         icon: Users,          comingSoon: true },
            { to: '/hr',          label: 'HR',              icon: HeartHandshake, comingSoon: true },
            { to: '/timesheets',  label: 'Timesheets',      icon: Clock,          comingSoon: true },
        ],
    },
    {
        label: 'Lead Gen',
        items: [
            { to: '/leadgen/discover',  label: 'Lead Discovery', icon: SearchIcon, perm: 'leadgen' },
            { to: '/leadgen/leads',     label: 'Leads Database', icon: Database,   perm: 'leadgen' },
            { to: '/leadgen/pipeline',  label: 'Pipeline',       icon: GitBranch,  perm: 'leadgen' },
            { to: '/leadgen/import',    label: 'Import Prospects', icon: Upload,  perm: 'leadgen' },
            { to: '/leadgen/email-campaigns', label: 'Email Campaigns', icon: Send, perm: 'leadgen' },
            { to: '/leadgen/inbox',     label: 'Inbox',          icon: InboxIcon,  perm: 'leadgen' },
        ],
    },
    {
        label: 'Settings',
        items: [
            { to: '/settings', label: 'Settings', icon: Settings, perm: 'settings' },
        ],
    },
];

const STORAGE_KEY = 'bm_sidebar_collapsed';

export default function Sidebar({ mobileOpen = false, onCloseMobile }) {
    const { user, logout, can } = useAuth();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
    });

    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0'); } catch { /* noop */ }
    }, [collapsed]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const handleNavClick = () => { if (onCloseMobile) onCloseMobile(); };

    // On mobile the drawer is always shown expanded (icons + labels); collapse only applies to md+ static layout.
    const effectiveCollapsed = collapsed && !mobileOpen;
    const desktopWidthClass = collapsed ? 'md:w-[68px]' : 'md:w-[240px]';

    return (
        <>
            {/* Mobile backdrop — tapping it closes the drawer */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/40 md:hidden"
                    onClick={onCloseMobile}
                    data-testid="sidebar-mobile-backdrop"
                />
            )}
            <aside
                className={`flex flex-col w-[240px] ${desktopWidthClass} shrink-0 h-full transition-transform md:transition-[width] duration-200 ease-in-out
                    fixed md:static inset-y-0 left-0 z-50
                    ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
                style={{ background: 'var(--bm-sidebar)', color: 'var(--bm-sidebar-text)' }}
                data-testid="sidebar"
                data-collapsed={collapsed ? 'true' : 'false'}
                data-mobile-open={mobileOpen ? 'true' : 'false'}
            >
            {/* Brand + toggle */}
            <div className={`px-3 py-4 border-b border-white/10 flex items-center gap-2 ${effectiveCollapsed ? 'flex-col' : 'justify-between'}`}>
                <button
                    onClick={() => setCollapsed(v => !v)}
                    className="hidden md:flex h-9 w-9 rounded-md hover:bg-white/10 items-center justify-center text-white/80 hover:text-white transition shrink-0"
                    title={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    data-testid="sidebar-toggle-btn"
                >
                    {effectiveCollapsed ? <Menu size={18} /> : <ChevronLeft size={18} />}
                </button>
                {!effectiveCollapsed && (
                    <div className="flex items-center gap-3 flex-1 min-w-0 justify-between">
                        <BrandLogo variant="short" onDark />
                        <NotificationBell />
                    </div>
                )}
                {effectiveCollapsed && (
                    <NotificationBell />
                )}
                <button
                    onClick={onCloseMobile}
                    className="md:hidden h-9 w-9 rounded-md hover:bg-white/10 flex items-center justify-center text-white/80 hover:text-white transition shrink-0"
                    aria-label="Close navigation menu"
                    data-testid="sidebar-mobile-close-btn"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto px-2 py-3" data-testid="sidebar-nav">
                {NAV_GROUPS.map((group, gi) => {
                    // Filter items the user can't access (keep `comingSoon` items visible as placeholders)
                    const visibleItems = group.items.filter(it => it.comingSoon || !it.perm || can(it.perm, 'view'));
                    if (visibleItems.length === 0) return null;
                    return (
                    <div key={gi}>
                        {/* Divider between groups */}
                        {gi > 0 && (
                            <div className="my-2 mx-1" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} />
                        )}

                        {/* Section label */}
                        {group.label && !effectiveCollapsed && (
                            <div
                                className="px-3 mb-1 mt-1"
                                style={{
                                    fontSize: '9px',
                                    fontWeight: 600,
                                    letterSpacing: '0.09em',
                                    textTransform: 'uppercase',
                                    color: 'rgba(255,255,255,0.38)',
                                    userSelect: 'none',
                                }}
                            >
                                {group.label}
                            </div>
                        )}

                        {/* Items */}
                        <div className="space-y-0.5">
                            {visibleItems.map((item) => {
                                const Icon = item.icon;
                                if (item.comingSoon) {
                                    return (
                                        <div
                                            key={item.to}
                                            title={effectiveCollapsed ? `${item.label} (coming soon)` : undefined}
                                            className={`bm-nav-link opacity-50 cursor-not-allowed ${effectiveCollapsed ? 'justify-center' : ''}`}
                                            data-testid={`nav-link-${item.to.slice(1)}`}
                                            aria-disabled="true"
                                        >
                                            <Icon />
                                            {!effectiveCollapsed && (
                                                <span className="flex items-center gap-2 flex-1 min-w-0">
                                                    <span className="truncate">{item.label}</span>
                                                    <span style={{
                                                        fontSize: '9px',
                                                        fontWeight: 700,
                                                        letterSpacing: '0.04em',
                                                        textTransform: 'uppercase',
                                                        background: 'rgba(245,158,11,0.25)',
                                                        color: '#fbbf24',
                                                        borderRadius: '4px',
                                                        padding: '1px 5px',
                                                        lineHeight: '14px',
                                                        whiteSpace: 'nowrap',
                                                        flexShrink: 0,
                                                    }}>Soon</span>
                                                </span>
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        onClick={handleNavClick}
                                        title={effectiveCollapsed ? item.label : undefined}
                                        className={({ isActive }) =>
                                            `bm-nav-link ${isActive ? 'active' : ''} ${effectiveCollapsed ? 'justify-center' : ''}`
                                        }
                                        data-testid={`nav-link-${item.to.slice(1)}`}
                                    >
                                        <Icon />
                                        {!effectiveCollapsed && <span>{item.label}</span>}
                                    </NavLink>
                                );
                            })}
                        </div>
                    </div>
                    );
                })}
            </nav>

            {/* User footer */}
            <div className="px-2 py-3 border-t border-white/10">
                {effectiveCollapsed ? (
                    <div className="flex flex-col items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center font-semibold text-white text-sm" title={user?.email}>
                            {(user?.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <button
                            onClick={handleLogout}
                            className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white transition"
                            title="Sign out"
                            data-testid="logout-btn"
                        >
                            <LogOut size={16} />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-3 px-2 py-2">
                        <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center font-semibold text-white text-sm">
                            {(user?.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] text-white truncate font-medium">{user?.name || 'User'}</div>
                            <div className="text-[11px] text-white/60 truncate">{user?.email}</div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white transition"
                            title="Sign out"
                            data-testid="logout-btn"
                        >
                            <LogOut size={16} />
                        </button>
                    </div>
                )}
            </div>
        </aside>
        </>
    );
}
